
/*********************************************************************
 *
 * WARNING: This shader class is automatically generated, any manual
 * edits will be lost.
 *
 ********************************************************************/
import { ShaderBase, ShaderUniformsContainer, ShaderAttributesContainer } from '../shader';
import { Texture, CubeMapTexture } from '../../texture';
import { Context } from '../../context/Context';
import { mat3, mat4, vec2, vec3, vec4 } from "gl-matrix";
import { ShaderSourceParser } from '../shader-source-parser';

export type AerialPerspectiveShaderUniforms = {
	uColorTexture: WebGLUniformLocation,
	uDepthTexture: WebGLUniformLocation,
	uInvViewProjection: WebGLUniformLocation,
	uCameraPosition: WebGLUniformLocation,
	uSunDirection: WebGLUniformLocation,
	uSunIntensity: WebGLUniformLocation,
	uTintColor: WebGLUniformLocation,
	uFogColor: WebGLUniformLocation,
	uHorizonMist: WebGLUniformLocation,
	uExposure: WebGLUniformLocation,
	uDensity: WebGLUniformLocation,
	uStartDistance: WebGLUniformLocation,
	uMaxBlend: WebGLUniformLocation
};

export type AerialPerspectiveShaderAttributes = {
	aVertexPosition: number,
	vTexCoord: number
};

export type AerialPerspectiveShaderSettings = string[];

export class AerialPerspectiveShader extends ShaderBase<AerialPerspectiveShaderUniforms, AerialPerspectiveShaderAttributes> {

    private static fragmentShaderSource = `
// Aerial perspective: blend opaque scene pixels toward the same atmospheric
// scattering value the procedural sky dome shows in their direction, with
// strength rising exponentially with view distance.
//
// Why: at km-scale outdoor scenes the LOD3 ring's silhouette/peaks drift as
// the camera moves, and the outermost ring also produces a hard horizon
// clip line where it ends and the sky begins. Wrapping both in a continuous
// distance-haze hides the LOD drift AND smooths the clip line into the sky,
// without looking "foggy" on a clear day — the colour comes from the same
// scattering model the dome uses, so the transition reads as physical.

// --- begin: world_pos_from_depth.glsl ---
// Reconstruct world / view position from a 24-bit depth sample, screen-space
// UV, and an inverse projection matrix. Replaces an explicit RGBA32F position
// attachment in the G-buffer (~16 bytes/pixel saved).
//
// Reversed-Z: the depth buffer maps near → 1.0 and far → 0.0. Cleared depth
// (= 0.0) means "no geometry was drawn" — callers should discard or treat this
// as background BEFORE calling these functions. The reconstruction itself
// still works at depth == 0.0 (it returns the far-plane point), but the
// result is rarely useful for lighting/AO/decals.
//
// The math is reversal-agnostic: invViewProjection is the inverse of whichever
// projection the depth buffer was rendered with, and the perspective divide
// recovers the correct world position regardless of which side of the NDC z
// range each plane maps to.

// Inputs:
//   depth              — raw [0,1] value from texture(uDepthMap, uv).r
//   uv                 — screen-space UV (vTexCoord, in [0,1])
//   invViewProjection  — inverse of (projection * view) for the current camera
vec3 worldPosFromDepth(float depth, vec2 uv, mat4 invViewProjection) {
	vec4 ndc = vec4(uv * 2.0 - 1.0, depth * 2.0 - 1.0, 1.0);
	vec4 world = invViewProjection * ndc;
	return world.xyz / world.w;
}

// View-space variant — pass invProjection (inverse of camera.projectionMatrix)
// when you only need view space. Skips the worldPos → viewPos round-trip,
// which matters for SSAO that samples 16+ positions per fragment.
vec3 viewPosFromDepth(float depth, vec2 uv, mat4 invProjection) {
	vec4 ndc = vec4(uv * 2.0 - 1.0, depth * 2.0 - 1.0, 1.0);
	vec4 view = invProjection * ndc;
	return view.xyz / view.w;
}

// --- end: world_pos_from_depth.glsl ---
// --- begin: atmosphere.glsl ---
// Single-scattering Rayleigh + Mie integration through a spherical atmosphere
// shell. Shared by:
//   - procedural-sky: sky dome colour as a function of view direction.
//   - aerial-perspective: distance-based haze that blends opaque scene pixels
//     toward the same scattering value, so far-LOD shape drift, tile-edge
//     mismatches, and the hard horizon clip all wash into atmosphere.
//
// Inputs use the well-known Nishita / "glsl-atmosphere" formulation. The
// virtual observer sits just above the planet surface — the shader's actual
// world position doesn't matter because scattering is a pure function of the
// view direction relative to the planet centre.

const float ATMOSPHERE_PI = 3.14159265358979;

const float PLANET_RADIUS = 6371000.0;
const float ATMOS_RADIUS  = 6471000.0;
const vec3  RAY_BETA      = vec3(5.5e-6, 13.0e-6, 22.4e-6); // Rayleigh scattering coefficients
const vec3  MIE_BETA      = vec3(21e-6);                    // Mie scattering coefficient
const float HEIGHT_RAY    = 8000.0;
const float HEIGHT_MIE    = 1200.0;
const float G             = 0.758; // Mie phase asymmetry

const int STEPS_PRIMARY = 16;
const int STEPS_LIGHT   = 8;

// Returns t for the far intersection of a ray with a sphere centred at the
// origin, or -1.0 if there is no intersection.
float raySphereFar(vec3 origin, vec3 dir, float radius) {
	float b = dot(origin, dir);
	float c = dot(origin, origin) - radius * radius;
	float d = b * b - c;
	if (d < 0.0) return -1.0;
	return -b + sqrt(d);
}

// Atmospheric scattering integrated along the primary ray, capped at maxDist
// metres. The sky dome passes a huge value (full column to outer atmosphere);
// aerial perspective passes the camera-to-pixel distance so opaque surfaces
// only receive in-scattering from the air actually between camera and
// surface — without the cap a 2km mountain peak picks up the same Mie sun
// peak as the 100km sky behind it. Caller multiplies by uSunIntensity.
vec3 atmosphere(vec3 rayDir, vec3 rayOrigin, vec3 sunDir, float maxDist) {
	float atmosDist = raySphereFar(rayOrigin, rayDir, ATMOS_RADIUS);
	if (atmosDist < 0.0) return vec3(0.0);
	atmosDist = min(atmosDist, maxDist);

	float stepSize = atmosDist / float(STEPS_PRIMARY);

	float mu = dot(rayDir, sunDir);
	float mu2 = mu * mu;
	float gg = G * G;
	float phaseRay = 3.0 / (16.0 * ATMOSPHERE_PI) * (1.0 + mu2);
	float phaseMie = 3.0 / (8.0 * ATMOSPHERE_PI)
		* ((1.0 - gg) * (mu2 + 1.0))
		/ (pow(1.0 + gg - 2.0 * mu * G, 1.5) * (2.0 + gg));

	vec3 sumRay = vec3(0.0);
	vec3 sumMie = vec3(0.0);
	float odRay = 0.0;
	float odMie = 0.0;

	for (int i = 0; i < STEPS_PRIMARY; i++) {
		vec3 sp = rayOrigin + rayDir * (float(i) + 0.5) * stepSize;
		float h = length(sp) - PLANET_RADIUS;
		float hr = exp(-h / HEIGHT_RAY) * stepSize;
		float hm = exp(-h / HEIGHT_MIE) * stepSize;
		odRay += hr;
		odMie += hm;

		// Light ray from sample point toward the sun.
		float lDist = raySphereFar(sp, sunDir, ATMOS_RADIUS);
		float lStep = lDist / float(STEPS_LIGHT);
		float lOdRay = 0.0;
		float lOdMie = 0.0;
		bool blocked = false;
		for (int j = 0; j < STEPS_LIGHT; j++) {
			vec3 lp = sp + sunDir * (float(j) + 0.5) * lStep;
			float lh = length(lp) - PLANET_RADIUS;
			if (lh < 0.0) { blocked = true; break; }
			lOdRay += exp(-lh / HEIGHT_RAY) * lStep;
			lOdMie += exp(-lh / HEIGHT_MIE) * lStep;
		}

		if (!blocked) {
			vec3 tau = RAY_BETA * (odRay + lOdRay) + MIE_BETA * 1.1 * (odMie + lOdMie);
			vec3 attn = exp(-tau);
			sumRay += hr * attn;
			sumMie += hm * attn;
		}
	}

	return phaseRay * RAY_BETA * sumRay + phaseMie * MIE_BETA * sumMie;
}

// --- end: atmosphere.glsl ---

in vec2 vTexCoord;
out vec4 fragColor;

uniform sampler2D uColorTexture;
uniform sampler2D uDepthTexture;
uniform mat4 uInvViewProjection;
uniform vec3 uCameraPosition;

// Atmosphere uniforms — kept in lock-step with ProceduralSky so the haze
// colour matches the dome behind it byte-for-byte at any direction.
uniform vec3 uSunDirection;     // direction TOWARD the sun, world space, normalized
uniform float uSunIntensity;
uniform vec3 uTintColor;
uniform vec3 uFogColor;
uniform float uHorizonMist;
uniform float uExposure;

// Haze tuning.
uniform float uDensity;       // exponential extinction per metre
uniform float uStartDistance; // no haze closer than this (player vicinity stays crisp)
uniform float uMaxBlend;      // upper cap on haze opacity (1.0 = full atmosphere)

void main(void) {
	float depth = texture(uDepthTexture, vTexCoord).r;
	vec4 sceneColor = texture(uColorTexture, vTexCoord);

	// Sky pixels (no opaque geometry) already show the procedural sky — leave them.
	// Reversed-Z: cleared depth = 0.0 means no opaque geometry.
	if (depth <= 0.0) {
		fragColor = sceneColor;
		return;
	}

	vec3 worldPos = worldPosFromDepth(depth, vTexCoord, uInvViewProjection);
	vec3 toPixel = worldPos - uCameraPosition;
	float dist = length(toPixel);
	vec3 viewDir = toPixel / max(dist, 1e-3);

	float fadeDist = max(dist - uStartDistance, 0.0);
	float t = 1.0 - exp(-uDensity * fadeDist);
	t = min(t, uMaxBlend);
	if (t <= 0.001) {
		fragColor = sceneColor;
		return;
	}

	// Match procedural-sky's virtual observer — vertical axis, just above
	// the planet surface — so the scattering value is continuous with the
	// dome at any view direction. Clamp the integration to the actual
	// camera-to-pixel distance: sky-at-infinity in-scattering would paint
	// the full Mie sun peak onto a 2km mountain silhouette, so a tall
	// distant peak between camera and sun reads as glowing orange. Limiting
	// the integral to the air actually between camera and surface gives the
	// physical aerial-perspective result instead.
	vec3 origin = vec3(0.0, PLANET_RADIUS + 1.0, 0.0);
	vec3 atm = uSunIntensity * atmosphere(viewDir, origin, normalize(uSunDirection), dist);

	// Same horizon mist + Reinhard tonemap + day/night tint as the sky shader.
	float horizonT = smoothstep(-0.05, 0.20, viewDir.y);
	atm = mix(mix(uFogColor, atm, horizonT), atm, 1.0 - uHorizonMist);
	atm *= uExposure;
	atm = atm / (vec3(1.0) + atm);
	vec3 hazeColor = atm * uTintColor;

	fragColor = vec4(mix(sceneColor.rgb, hazeColor, t), sceneColor.a);
}
`;

    private static vertexShaderSource = `
// --- begin: fullscreen_quad.vert.glsl ---
layout(location = 0) in vec2 aVertexPosition;
out vec2 vTexCoord;

void main(void) {
	vTexCoord = aVertexPosition * 0.5 + 0.5;
	gl_Position = vec4(aVertexPosition, 0.0, 1.0);
}
// --- end: fullscreen_quad.vert.glsl ---
`;

    public override get version(): string {
        return '300 es';
    }

    public override get parser(): any {
        return new ShaderSourceParser();
    }

    protected override get warnAttributeMismatch(): boolean {
        return false;
    }

    public uniformsContainer: AerialPerspectiveShaderUniformsContainer;
    public attributesContainer: AerialPerspectiveShaderShaderAttributesContainer;

    constructor(ctx: Context, settings: AerialPerspectiveShaderSettings) {
        super(ctx, AerialPerspectiveShader.fragmentShaderSource, AerialPerspectiveShader.vertexShaderSource, settings);
        this.uniformsContainer = new AerialPerspectiveShaderUniformsContainer(ctx, this);
        this.attributesContainer = new AerialPerspectiveShaderShaderAttributesContainer(ctx, this);
    }
}

class AerialPerspectiveShaderUniformsContainer extends ShaderUniformsContainer {
    constructor(ctx: Context, private shader: AerialPerspectiveShader) {
        super(ctx);
    }

    public setColorTexture(texture: Texture) : AerialPerspectiveShaderUniformsContainer {
        this.setTextureValue('uColorTexture', this.shader.uniforms.uColorTexture, texture, 'sampler2D');
        return this;
    }

    public setDepthTexture(texture: Texture) : AerialPerspectiveShaderUniformsContainer {
        this.setTextureValue('uDepthTexture', this.shader.uniforms.uDepthTexture, texture, 'sampler2D');
        return this;
    }

    public setInvViewProjection(value: mat4 | number[]) : AerialPerspectiveShaderUniformsContainer {
        this.setValue('uInvViewProjection', this.shader.uniforms.uInvViewProjection, value, 'mat4');
        return this;
    }

    public setCameraPosition(value: vec3 | number[]) : AerialPerspectiveShaderUniformsContainer {
        this.setValue('uCameraPosition', this.shader.uniforms.uCameraPosition, value, 'vec3');
        return this;
    }

    public setSunDirection(value: vec3 | number[]) : AerialPerspectiveShaderUniformsContainer {
        this.setValue('uSunDirection', this.shader.uniforms.uSunDirection, value, 'vec3');
        return this;
    }

    public setSunIntensity(value: number) : AerialPerspectiveShaderUniformsContainer {
        this.setValue('uSunIntensity', this.shader.uniforms.uSunIntensity, value, 'float');
        return this;
    }

    public setTintColor(value: vec3 | number[]) : AerialPerspectiveShaderUniformsContainer {
        this.setValue('uTintColor', this.shader.uniforms.uTintColor, value, 'vec3');
        return this;
    }

    public setFogColor(value: vec3 | number[]) : AerialPerspectiveShaderUniformsContainer {
        this.setValue('uFogColor', this.shader.uniforms.uFogColor, value, 'vec3');
        return this;
    }

    public setHorizonMist(value: number) : AerialPerspectiveShaderUniformsContainer {
        this.setValue('uHorizonMist', this.shader.uniforms.uHorizonMist, value, 'float');
        return this;
    }

    public setExposure(value: number) : AerialPerspectiveShaderUniformsContainer {
        this.setValue('uExposure', this.shader.uniforms.uExposure, value, 'float');
        return this;
    }

    public setDensity(value: number) : AerialPerspectiveShaderUniformsContainer {
        this.setValue('uDensity', this.shader.uniforms.uDensity, value, 'float');
        return this;
    }

    public setStartDistance(value: number) : AerialPerspectiveShaderUniformsContainer {
        this.setValue('uStartDistance', this.shader.uniforms.uStartDistance, value, 'float');
        return this;
    }

    public setMaxBlend(value: number) : AerialPerspectiveShaderUniformsContainer {
        this.setValue('uMaxBlend', this.shader.uniforms.uMaxBlend, value, 'float');
        return this;
    }

    protected getTextureUniforms() {
        return [
			{ name: 'uColorTexture', uniform: this.shader.uniforms.uColorTexture, dataType: 'sampler2D' },
			{ name: 'uDepthTexture', uniform: this.shader.uniforms.uDepthTexture, dataType: 'sampler2D' }
        ];
    }
}

class AerialPerspectiveShaderShaderAttributesContainer extends ShaderAttributesContainer {
    constructor(ctx: Context, private shader: AerialPerspectiveShader) {
        super(ctx);
    }

    public setVertexPosition(settings: { length: number, offset: number, itemSize: number }) : AerialPerspectiveShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aVertexPosition, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvTexCoord(settings: { length: number, offset: number, itemSize: number }) : AerialPerspectiveShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vTexCoord, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }
}